/**
 * Statement-layout transport.
 *
 * A layout is how a statement is grouped: which accounts land on which line,
 * what rolls into what, and what is computed rather than aggregated. It is
 * DATA — regrouping the profit and loss account is a configuration change, not
 * a deployment.
 *
 * Three contract facts the screens are built on:
 *
 *   • A create makes a new VERSION of a code and never replaces its
 *     predecessor. Statements for earlier periods keep resolving to the version
 *     in force when they were reported, so evolving a presentation cannot
 *     restate history.
 *   • Only the name, notes and effective date may be patched. The code, type
 *     and version are what a filed statement cites as its provenance, so the
 *     database refuses to move them.
 *   • Validation always answers 200 — even when the layout is unusable — and
 *     carries every issue at once, because an operator fixing a chart of
 *     accounts needs to see every hole, not the first one.
 */
import type { components } from "@erp/api-client";

import { apiFetch } from "@/lib/api/http";

type Schemas = components["schemas"];

export type StatementLayout = Schemas["StatementLayout"];
export type StatementLayoutLine = Schemas["StatementLayoutLine"];
export type StatementLayoutRule = Schemas["StatementLayoutRule"];
export type StatementType = Schemas["StatementType"];
export type StatementLineKind = Schemas["StatementLineKind"];
export type StatementRuleKind = Schemas["StatementRuleKind"];
export type CashFlowActivity = Schemas["CashFlowActivity"];
export type StatementLayoutValidation = Schemas["StatementLayoutValidation"];
export type CreateStatementLayoutRequest = Schemas["CreateStatementLayoutRequest"];
export type UpdateStatementLayoutRequest = Schemas["UpdateStatementLayoutRequest"];

export const STATEMENT_TYPES: readonly StatementType[] = [
  "profit_and_loss",
  "balance_sheet",
  "cash_flow",
];

export const LINE_KINDS: readonly StatementLineKind[] = [
  "detail",
  "subtotal",
  "current_year_earnings",
  "net_income",
  "cash",
  "unclassified",
];

export const RULE_KINDS: readonly StatementRuleKind[] = [
  "account_code_range",
  "account_type",
  "account_tag",
];

export const CASH_FLOW_ACTIVITIES: readonly CashFlowActivity[] = [
  "operating",
  "investing",
  "financing",
];

/** Only `detail` and `cash` lines may carry account rules — every other kind is
 *  computed, and letting one map accounts too would count the same movement
 *  twice. */
export function kindMapsAccounts(kind: StatementLineKind): boolean {
  return kind === "detail" || kind === "cash";
}

/** Which line kinds each statement can use. A balance sheet has undistributed
 *  profit; only the cash-flow statement has a cash population and a reconciling
 *  line. */
export function kindsFor(statementType: StatementType): readonly StatementLineKind[] {
  switch (statementType) {
    case "profit_and_loss":
      return ["detail", "subtotal"];
    case "balance_sheet":
      return ["detail", "subtotal", "current_year_earnings"];
    case "cash_flow":
      return ["detail", "subtotal", "net_income", "cash", "unclassified"];
  }
}

export function listStatementLayouts(
  query: { statement_type?: StatementType } = {},
  signal?: AbortSignal,
): Promise<{ items: readonly StatementLayout[] }> {
  return apiFetch<{ items: readonly StatementLayout[] }>("/statement-layouts", { query, signal });
}

export function getStatementLayout(
  layoutId: string,
  signal?: AbortSignal,
): Promise<StatementLayout> {
  return apiFetch<StatementLayout>(`/statement-layouts/${encodeURIComponent(layoutId)}`, {
    signal,
  });
}

export function createStatementLayout(
  body: CreateStatementLayoutRequest,
  idempotencyKey: string,
): Promise<StatementLayout> {
  return apiFetch<StatementLayout>("/statement-layouts", {
    method: "POST",
    body,
    idempotencyKey,
  });
}

/** Presentation metadata only. Sending a code, type or version is refused by
 *  the contract, so this never offers them. */
export function updateStatementLayout(
  layoutId: string,
  body: UpdateStatementLayoutRequest,
): Promise<StatementLayout> {
  return apiFetch<StatementLayout>(`/statement-layouts/${encodeURIComponent(layoutId)}`, {
    method: "PATCH",
    body,
  });
}

export function validateStatementLayout(
  layoutId: string,
  query: { fiscal_period_id?: string } = {},
  signal?: AbortSignal,
): Promise<StatementLayoutValidation> {
  return apiFetch<StatementLayoutValidation>(
    `/statement-layouts/${encodeURIComponent(layoutId)}/validation`,
    { query, signal },
  );
}

export interface LayoutTreeNode {
  line: StatementLayoutLine;
  depth: number;
}

/**
 * Flattens a layout's lines into display order: each subtotal followed by what
 * rolls into it. Lines are ordered by `ordinal` within their parent, and a line
 * whose parent code is missing is shown at the root rather than dropped —
 * hiding a line because its parent is wrong is how a hole goes unnoticed.
 */
export function layoutTree(lines: readonly StatementLayoutLine[]): LayoutTreeNode[] {
  const byParent = new Map<string, StatementLayoutLine[]>();
  const codes = new Set(lines.map((line) => line.code));
  for (const line of lines) {
    const parent =
      line.parent_code !== undefined && codes.has(line.parent_code) ? line.parent_code : "";
    const siblings = byParent.get(parent) ?? [];
    siblings.push(line);
    byParent.set(parent, siblings);
  }
  const nodes: LayoutTreeNode[] = [];
  const walk = (parent: string, depth: number, guard: Set<string>) => {
    const siblings = [...(byParent.get(parent) ?? [])].sort(
      (left, right) => left.ordinal - right.ordinal,
    );
    for (const line of siblings) {
      if (guard.has(line.code)) continue; // a cycle is an issue, not a hang
      nodes.push({ line, depth });
      walk(line.code, depth + 1, new Set([...guard, line.code]));
    }
  };
  walk("", 0, new Set());
  return nodes;
}

/** A one-line summary of what a rule selects, for a table cell. */
export function describeRule(rule: StatementLayoutRule): string {
  switch (rule.kind) {
    case "account_code_range":
      return `${rule.code_from ?? "…"} – ${rule.code_to ?? "…"}`;
    case "account_type":
      return rule.account_type ?? "";
    case "account_tag":
      return rule.account_tag ?? "";
    default:
      return "";
  }
}
